import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import { TableSkeleton } from '../components/shared/LoaderSkeleton';
import Text from '../components/shared/Text';

// Premium Purchases Index - Architectural Alignment with InvoiceShow
function PurchasesIndex() {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        total_pages: 1,
        total_count: 0,
        current_page: 1,
        per_page: 25
    });

    useEffect(() => {
        loadPurchases();
    }, [search, currentPage]);

    const loadPurchases = async () => {
        try {
            setLoading(true);
            const response = await api.getPurchases({ q: search, page: currentPage });
            if (response.success) {
                setPurchases(Array.isArray(response.data) ? response.data : (response.data.purchases || []));
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && purchases.length === 0) return <TableSkeleton />;

    // Calculate quick stats from current page for visual flair
    const totalValue = purchases.reduce((sum: number, p: any) => sum + (p.total_amount || 0), 0);
    const totalDue = purchases.reduce((sum: number, p: any) => sum + (p.balance_due || 0), 0);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-slate-50/30 min-h-screen">
            {/* Header / Actions - Styled like InvoiceShow */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <Text variant="h1" color="primary" className="tracking-tight">
                            Procurement <span className="text-indigo-600">Registry</span>
                        </Text>
                        <span className="px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {pagination.total_count} Records
                        </span>
                    </div>
                    <div className="ml-10">
                        <Text variant="body" color="slate">
                            Managing inbound <span className="font-semibold text-slate-700">supply chain operations</span>
                        </Text>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Link
                        to="/purchases/new"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-200"
                    >
                        <svg className="w-4 h-4 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                        New Entry
                    </Link>
                </div>
            </div>

            {/* Quick Stats - Segmented Style */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-slate-100 mb-8">
                <div className="p-5 flex-1 group">
                    <Text variant="label" color="slate" className="mb-1 block">Periodic Value (Page)</Text>
                    <Text variant="h2" color="primary" className="font-mono">₹{totalValue.toLocaleString()}</Text>
                    <Text variant="small" weight="bold" color="slate" className="mt-1 block uppercase tracking-tight">Gross Acquisition</Text>
                </div>
                <div className="p-5 flex-1 group">
                    <Text variant="label" color="slate" className="mb-1 block">Periodic Liability</Text>
                    <Text variant="h2" className="font-mono text-rose-600">₹{totalDue.toLocaleString()}</Text>
                    <Text variant="small" weight="bold" color="slate" className="mt-1 block uppercase tracking-tight">Pending Settlements</Text>
                </div>
                <div className="p-5 flex-1 group">
                    <Text variant="label" color="slate" className="mb-1 block">Cash Allocation</Text>
                    <Text variant="h2" className="font-mono text-emerald-600">₹{(totalValue - totalDue).toLocaleString()}</Text>
                    <Text variant="small" weight="bold" color="slate" className="mt-1 block uppercase tracking-tight">Settled Procurement</Text>
                </div>
                <div className="p-5 flex-1 group">
                    <Text variant="label" color="slate" className="mb-1 block">Operating Density</Text>
                    <Text variant="h2" className="font-mono text-indigo-600">{purchases.length}</Text>
                    <Text variant="small" weight="bold" color="slate" className="mt-1 block uppercase tracking-tight">Active Batch Items</Text>
                </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="mb-6">
                <div className="relative group">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search procurement history by vendor or number..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-11 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm font-medium transition-all shadow-sm"
                    />
                </div>
            </div>

            {/* Table Style Feed */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-8 transition-all hover:shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-4"><Text variant="label" color="slate">Reference</Text></th>
                                <th className="px-6 py-4"><Text variant="label" color="slate">Origin / Vendor</Text></th>
                                <th className="px-6 py-4"><Text variant="label" color="slate">Operational Date</Text></th>
                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Total (Incl. Tax)</Text></th>
                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Liability</Text></th>
                                <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Action</Text></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {purchases.map((purchase: any) => (
                                <tr key={purchase.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Link to={`/purchases/${purchase.id}`}>
                                            <Text weight="bold" className="text-indigo-600 hover:text-indigo-800 transition-colors">
                                                #{purchase.purchase_number}
                                            </Text>
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Text variant="body" weight="semibold" color="primary">{purchase.vendor_name}</Text>
                                        <Text variant="label" color="slate" className="tracking-tight">Verified Entity</Text>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Text variant="body" color="primary" weight="medium">
                                            {new Date(purchase.purchase_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </Text>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Text variant="mono" weight="bold" color="primary" className="tracking-tighter">
                                            ₹{purchase.total_amount?.toLocaleString()}
                                        </Text>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Text variant="mono" weight="bold" className={`tracking-tighter ${purchase.balance_due > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                            ₹{purchase.balance_due?.toLocaleString()}
                                        </Text>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <Link
                                            to={`/purchases/${purchase.id}`}
                                            className="inline-flex p-1.5 text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all group-hover:text-slate-500"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {purchases.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-300 mb-3">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                                            </div>
                                            <Text variant="label" color="slate" className="tracking-widest uppercase">No Records Identified</Text>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Alignment */}
            <div className="flex justify-end pt-2">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

export default PurchasesIndex;
